import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MessageCircle, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
    const { userId } = useParams();
    const [params] = useSearchParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [threads, setThreads] = useState([]);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [otherName, setOtherName] = useState("");
    const endRef = useRef(null);
    const roomId = params.get("room") || "";

    const loadThreads = async () => {
        try {
            const { data } = await api.get("/messages/threads");
            setThreads(data);
        } catch { /* ignore */ }
    };

    useEffect(() => { loadThreads(); }, []);

    useEffect(() => {
        if (!userId) return;
        (async () => {
            try {
                const { data } = await api.get(`/messages/${userId}`);
                setMessages(data);
                if (data.length) {
                    const other = data[0].sender_id === userId ? data[0].sender_name : data[0].recipient_name;
                    setOtherName(other);
                } else {
                    // If no messages yet, try to fetch the counterpart name from threads
                    const t = threads.find((x) => x.user_id === userId);
                    if (t) setOtherName(t.user_name);
                }
            } catch { /* ignore */ }
        })();
    }, [userId, threads]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const send = async (e) => {
        e.preventDefault();
        if (!text.trim() || !userId) return;
        setSending(true);
        try {
            const { data } = await api.post("/messages", {
                recipient_id: userId, content: text.trim(), room_id: roomId,
            });
            setMessages((m) => [...m, data]);
            setText("");
            loadThreads();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally { setSending(false); }
    };

    // Mobile: show a single view. If a userId is in the URL, show the chat.
    // Otherwise show the thread list. Desktop shows both side-by-side.
    const showChat = Boolean(userId);

    return (
        <div data-testid="messages-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16 sm:py-10">
            {/* ── Hero (hidden on mobile chat view for a fullscreen chat feel) ── */}
            {!showChat && (
                <motion.section
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="relative overflow-hidden rounded-3xl p-6 sm:p-8 text-white shadow-xl"
                    style={{ background: "linear-gradient(135deg, #f4a261 0%, #dd6b4d 55%, #b74e33 100%)" }}
                >
                    <div className="absolute -right-14 -top-14 w-56 h-56 rounded-full bg-white/15 blur-2xl" aria-hidden />
                    <div className="relative">
                        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/80 inline-flex items-center gap-2">
                            <MessageCircle className="w-3.5 h-3.5" /> Messages
                        </div>
                        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2 leading-tight">
                            Say hi to your matches
                        </h1>
                        <p className="text-white/85 mt-2 text-sm sm:text-base max-w-lg">
                            Chat with people going to your city, or heading to somewhere you love.
                        </p>
                    </div>
                </motion.section>
            )}

            <div className={`mt-6 grid gap-6 md:grid-cols-12 ${showChat ? "" : "md:mt-8"}`}>
                {/* ── Thread list ─────────────────────────────────── */}
                <aside className={`md:col-span-4 ${showChat ? "hidden md:block" : ""}`}>
                    <div className="bg-white border border-border/60 rounded-3xl overflow-hidden">
                        <div className="px-5 py-4 border-b border-border/50 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                            Conversations
                        </div>
                        {threads.length === 0 ? (
                            <div className="p-8 text-center" data-testid="no-threads">
                                <div className="w-14 h-14 mx-auto rounded-2xl bg-muted flex items-center justify-center">
                                    <Sparkles className="w-6 h-6 text-muted-foreground" />
                                </div>
                                <p className="mt-3 text-sm text-muted-foreground">No conversations yet.<br/>Open a room and tap "Send message".</p>
                            </div>
                        ) : (
                            <ul>
                                {threads.map((t, i) => (
                                    <motion.li
                                        key={t.user_id}
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.04, duration: 0.3 }}
                                    >
                                        <button
                                            data-testid={`thread-${t.user_id}`}
                                            onClick={() => navigate(`/messages/${t.user_id}`)}
                                            className={`w-full text-left px-5 py-3.5 border-b border-border/40 hover:bg-muted transition-colors flex items-center gap-3 ${userId === t.user_id ? "bg-muted" : ""}`}
                                        >
                                            <div className="w-11 h-11 rounded-full bg-primary/10 text-primary font-display font-bold text-lg flex items-center justify-center shrink-0">
                                                {(t.user_name || "?").charAt(0).toUpperCase()}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="font-semibold truncate">{t.user_name}</div>
                                                    {t.unread > 0 && (
                                                        <span className="bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center shrink-0">{t.unread}</span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-muted-foreground truncate mt-0.5">{t.last_message}</div>
                                            </div>
                                        </button>
                                    </motion.li>
                                ))}
                            </ul>
                        )}
                    </div>
                </aside>

                {/* ── Chat view ───────────────────────────────────── */}
                <section className={`md:col-span-8 ${showChat ? "" : "hidden md:block"}`}>
                    {!userId ? (
                        <div className="hidden md:flex bg-white border border-border/60 rounded-3xl min-h-[480px] items-center justify-center text-center p-8" data-testid="select-thread-hint">
                            <div>
                                <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
                                    <MessageCircle className="w-8 h-8 text-primary" />
                                </div>
                                <p className="mt-4 text-muted-foreground">Pick a conversation on the left to read.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white border border-border/60 rounded-3xl overflow-hidden flex flex-col" style={{ minHeight: 520 }}>
                            <div className="px-4 sm:px-5 py-3.5 border-b border-border/50 flex items-center gap-3 bg-gradient-to-r from-primary/5 to-transparent">
                                <button
                                    onClick={() => navigate("/messages")}
                                    data-testid="chat-back-btn"
                                    className="md:hidden w-9 h-9 rounded-full hover:bg-muted flex items-center justify-center shrink-0 active:scale-95 transition-transform"
                                    aria-label="Back to conversations"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                </button>
                                <div className="w-10 h-10 rounded-full bg-primary/15 text-primary font-display font-bold flex items-center justify-center shrink-0">
                                    {(otherName || "?").charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Chat with</div>
                                    <div className="font-display text-base sm:text-lg font-semibold truncate" data-testid="thread-with">
                                        {otherName || "Student"}
                                    </div>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5 bg-muted/20" style={{ maxHeight: 480 }}>
                                {messages.length === 0 && (
                                    <div className="text-center text-sm text-muted-foreground py-10">
                                        Start the conversation — say hi 👋
                                    </div>
                                )}
                                {messages.map((m) => {
                                    const mine = m.sender_id === user?.id;
                                    return (
                                        <motion.div
                                            key={m.id}
                                            initial={{ opacity: 0, y: 6 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className={`flex ${mine ? "justify-end" : "justify-start"}`}
                                        >
                                            <div
                                                data-testid={`message-${m.id}`}
                                                className={`max-w-[80%] sm:max-w-md px-4 py-2.5 text-sm shadow-sm ${mine ? "bg-primary text-primary-foreground rounded-2xl rounded-br-md" : "bg-white border border-border rounded-2xl rounded-bl-md"}`}
                                            >
                                                {m.content}
                                            </div>
                                        </motion.div>
                                    );
                                })}
                                <div ref={endRef} />
                            </div>

                            <form onSubmit={send} className="p-3 sm:p-4 border-t border-border/50 flex gap-2 bg-background/40">
                                <Input
                                    data-testid="message-input"
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder="Write a message…"
                                    className="rounded-full h-11 bg-white"
                                />
                                <Button
                                    type="submit"
                                    disabled={sending || !text.trim()}
                                    data-testid="message-send-btn"
                                    className="rounded-full bg-primary hover:bg-primary/90 h-11 w-11 p-0 shrink-0"
                                    aria-label="Send"
                                >
                                    <Send className="w-4 h-4" />
                                </Button>
                            </form>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
